import { action, observable } from 'mobx';
import stores from 'cd-eft/models/Stores';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { EftProjUserInputController } from 'cd-eft/services/eftPrjAdminApi';
import { DesignSurfaceRequest } from 'cd-eft/_codegen_/openapi-generator/eft-set';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';
import MapView from '@arcgis/core/views/MapView';
import { DesignElementController, TerraceDesignToolsController, WaterwayDesignToolsController, StructureController, TerraceReachController } from 'cd-eft/services/eftSetApi';
import { toTitleCase } from '../../../../utils/stringUtilities';
import { DesignContainerVM } from 'cd-eft/components/Design/DesignContainer/DesignContainerVM';
import { TerraceUiState, UIState } from 'cd-eft/models/Stores/TerraceDesignStore';
import { terraceTypes } from '../TerraceContainer/TerraceContainerVM';
import { appConfig } from 'cd-eft/config/AppConfig';
import { GroupLayer } from 'cd-eft/utils/esriTypes';

export interface ITerraceSummaryStation {
  select: boolean;
  name: string;
  defaultTemplate: string;
  length: number;
  maxHeight: number;
  terraceType: string;
  alignedDesignElementId: number;
  lastUpdated: number;
  createdDate?: number;
  warnings: string;
}

interface IAllNotifications {
  surface: number,
  alignment: number,
  hydrology: number,
  designControl: number,
  reaches: number,
}

const { designStore, terraceDesignStore: terraceStore } = stores.AppStore;

export class TerraceSummaryVM {
  private mapView: MapView | undefined;
  @observable history;

  constructor(history) {
    this.history = history;
    if (EngineeringMapService.instance?.mapView) {
      this.mapView = EngineeringMapService.instance.mapView;
    }
  }

  @observable TerraceSummaryStationList: ITerraceSummaryStation[] = [];
  @observable notificationDictionary: { [key: number]: IAllNotifications | null } = {};
  @observable listTerraceNotificationResponses: any[] = [];

  @observable headerTitle = "";
  @observable onClickInformation = false;
  @observable showIcon: boolean = false;
  @observable isLoading: boolean = false;

  @action.bound onClickInformationToggle = (title) => {
    this.headerTitle = title
    this.onClickInformation = !this.onClickInformation
  }

  @action.bound proceedToPage(page: 'input' | 'design' | 'template' | 'storage-calculation') {
    this.history.push(`/design/${designStore.projectsStore.currentProjectId}/terrace/${terraceStore.terraceDesignObject.alignedElementDesignParametersId}/${page}`)
  }

  @action.bound async callRebuildSurface() {
    await this.rebuildSurface();
  }

  @action.bound async rebuildSurface(fromDelete: boolean = false) {
    if (this.mapView && !fromDelete) {
      terraceStore.terraceLayers.forEach((layer) => {
        this.mapView!.map.remove(layer);
        layer.destroy();
      })
    }
    if (this.TerraceSummaryStationList.length > 0) {
      const alignedDesignElementIds: number[] = this.TerraceSummaryStationList.filter(e => e.select).map(e => e.alignedDesignElementId).filter(e => e !== -1); // filter out undefined
      try {
        designStore.setLoader("Rebuilding surface...");
        if (alignedDesignElementIds.length > 0) {
          const payload: DesignSurfaceRequest = {
            includeContour: true,
            sessionId: stores.AppStore.designStore.projectsStore.currentSessionId,
            structureDesElemIds: alignedDesignElementIds,
            linear: true
          }
          const response = await StructureController.getMultipleDesignSurfacesUsingPOST(payload);
          if (response && response.data) {
            if (response.data.designedContours) {
              const data = response.data;
              const tin = data.designedTinTriangles ? data.designedTinTriangles : [];
              await DesignContainerVM.loadSurfaceFromData(tin, undefined, data.designedContours, undefined, undefined);
            } else {
              console.error("API getMultipleDesignSurfacesUsingPOST response designedContours is null")
            }
          }
        } else {
          await DesignContainerVM.displayOriginalSurface();
        }
      } catch (e) {
        MessageCenter.addError((e as Error).message);
      } finally {
        designStore.setLoader("");
      }
    }
  }

  @action.bound init = () => {
    designStore.designElementTypeId = appConfig.hardCodes.designElementTypeTerrace;
  }

  @action.bound async getAllAlignmentsStatus() {
    const sessionId = designStore.projectsStore.currentSessionId
    try {
      const response = await DesignElementController.getDesignElementsBySessionAndTypeUsingGET(sessionId, "Alignment");
      if (response && response.data && response.data.designElementResponses) {
        const enableEdit = response.data.designElementResponses.some(item => item.flagUpToDate === false);
        this.elementActions.Edit.disabled = !enableEdit;
      }
    } catch (error) {
      console.error('getAllAlignmentsStatus error:', error);
    }
  }

  @action.bound async getTerraceSummary() {
    designStore.designElementTypeId = appConfig.hardCodes.designElementTypeTerrace; //To enable ToolBar on the map for Terrace
    terraceStore.summaryTerraceTypes = [];
    if (!designStore.projectsStore.currentSessionId) {
      return;
    }

    this.isLoading = true;

    try {
      const response = await TerraceDesignToolsController.getTerraceDesignSummariesBySessionUuidUsingGET(designStore.projectsStore.currentSessionId);

      if (response && response.data) {
        const data = response.data;
        this.TerraceSummaryStationList = data.map((e) => {
          const terraceTypeId = e.terraceType ? Number(Object.keys(terraceTypes).find((key) => terraceTypes[key] === e.terraceType!.charAt(0).toUpperCase() + e.terraceType!.slice(1).toLowerCase())) : 0;
          terraceStore.summaryTerraceTypes.push({ elementId: e.alignedElementDesignParamsId, type: terraceTypeId });
          const newSummary: ITerraceSummaryStation = {
            select: false,
            name: e.name ? e.name : "",
            terraceType: e.terraceType ? toTitleCase(e.terraceType) : "",
            defaultTemplate: e.defaultTemplate ? e.defaultTemplate : "",
            length: e.length ? parseFloat(e.length.toFixed(2)) : 0,
            maxHeight: e.maxHeight ? parseFloat(e.maxHeight.toFixed(2)) : 0,
            lastUpdated: e.lastChangeDate ? e.lastChangeDate : 0,
            createdDate: e.createdDate ? e.createdDate : 0,
            alignedDesignElementId: e.alignedElementDesignParamsId ? e.alignedElementDesignParamsId : -1,
            warnings: e.warnings ? e.warnings : ''
          }
          return newSummary
        })
        this.sortTerraceSummaryStationList();
      }
    }
    catch (error) {
      console.error(error);
    }
    finally {
      this.isLoading = false;
    }
  }

  @action.bound async getAllNotificationsForTerraces() {
    if (!stores.AppStore.designStore.projectsStore.currentSessionId) {
      return;
    }
    try {
      this.notificationDictionary = {};
      const zeroOnly: IAllNotifications = { surface: 0, alignment: 0, hydrology: 0, designControl: 0, reaches: 0, };
      const session = stores.AppStore.designStore.projectsStore.currentSessionId;
      const response = await WaterwayDesignToolsController.getAlignedElementDesignParamsIdsAndNotificationsBySessionUuidUsingGET(session); //add terrace type for new endpoint API
      if (response && response.data && response.data.alignedElementIdsAndNotificationsDtoList) {
        this.listTerraceNotificationResponses.push(response.data.alignedElementIdsAndNotificationsDtoList);
        this.showIcon = false;
        response.data.alignedElementIdsAndNotificationsDtoList.forEach((e) => {
          const terraceNotification: IAllNotifications = {
            surface: e.surfaceChange ? e.surfaceChange : 0,
            alignment: e.alignmentChange ? e.alignmentChange : 0,
            hydrology: e.hydroChange ? e.hydroChange : 0,
            designControl: e.alignedFeatureSCChange ? e.alignedFeatureSCChange : 0,
            reaches: e.reachesChange ? e.reachesChange : 0,
          }
          if (terraceNotification.surface !== 0 || terraceNotification.alignment !== 0 || terraceNotification.hydrology !== 0
            || terraceNotification.designControl !== 0 || terraceNotification.reaches !== 0) {
            this.notificationDictionary[e.alignedElementId!] = terraceNotification;
            this.showIcon = true;
          }
          else {
            this.notificationDictionary[e.alignedElementId!] = zeroOnly;
          }
        })
        if (response.data.validationStatusErrorCode) {
          await MessageCenter.addNotificationFromCode(response.data.validationStatusErrorCode);
        }
      }
    } catch (error) {
      console.error((error as Error).message);
    }
  }
  notificationStatusOR(elementId: number): boolean {
    const target = this.notificationDictionary[elementId];
    if (!target) {
      return false;
    }
    return !!target.alignment || !!target.designControl || !!target.hydrology || !!target.reaches || !!target.surface;
  }

  @action.bound sortTerraceSummaryStationList() {
    const sortedList = [...this.TerraceSummaryStationList].sort((a, b) => b.lastUpdated - a.lastUpdated);
    this.TerraceSummaryStationList = sortedList;
  }

  @action.bound async combinedLoad() {
    this.isLoading = true;
    if (this.mapView) {
      const alignmentSketchLayer = this.mapView.map.findLayerById('alignment_sketch');
      if (alignmentSketchLayer) this.mapView.map.remove(alignmentSketchLayer);
      alignmentSketchLayer?.destroy();
      const alignmentCurvedLayer = this.mapView.map.findLayerById('alignment_curved');
      if (alignmentCurvedLayer) this.mapView.map.remove(alignmentCurvedLayer);
      alignmentCurvedLayer?.destroy();
    }
    designStore.setLoader('Fetching data...', 'Terrace Design', 0, 30);
    designStore.setLoader('Getting notifications...', 'Terrace Design', 31, 60);
    //await this.getNotifications();
    await this.getAllNotificationsForTerraces();
    designStore.setLoader('Getting alignments status...', 'Terrace Design', 61, 70);
    await this.getAllAlignmentsStatus();
    designStore.setLoader('Retrieving terrace summary...', 'Terrace Design', 71, 90);
    await this.getTerraceSummary();
    if (terraceStore.newTerraces.length > 0) {
      terraceStore.newTerraces.forEach(el => {
        const item = this.TerraceSummaryStationList.find(t => +t.alignedDesignElementId === +el)
        item!.select = true;
      })
      await this.refreshElements();
    }
    designStore.setLoader('Finalizing...', 'Terrace Design', 91, 100);
    await setTimeout(() => {
      designStore.setLoader('');
      designStore.setLoader('');
      designStore.setLoader('');
      designStore.setLoader('');
      designStore.setLoader('');
    }, 2000)
    this.isLoading = false;
  }

  @action.bound async editSummaryStation(e: any, item: any) {
    terraceStore.terraceDesignObject.alignedFeatureName = item.name;
    terraceStore.terraceType = item.terraceType;
    const id = item.alignedDesignElementId;
    terraceStore.terraceDesignObject.alignedElementDesignParametersId = id;
    terraceStore.summaryInfo = {
      templateName: item.defaultTemplate,
      channelLength: item.length,
      terraceNameExists: true
    }
    try {
      const response = await EftProjUserInputController.getDesignUiStateUsingGET1(designStore.projectsStore.currentSessionId, id);

      if (response.data.errorCode === "Success") {
        // delete terrace from highlighted in summary
        if (terraceStore.newTerraces.includes(+id)) {
          terraceStore.newTerraces = terraceStore.newTerraces.filter(el => el !== +id);
        }

        if (response.data.alignedElementUiStateDto && response.data.alignedElementUiStateDto.uiSettingsDto) {

          const uiSettingsDto = response.data.alignedElementUiStateDto.uiSettingsDto;
          const savedProfileSettingsForm = terraceStore.savedProfileSettingsForm;
          savedProfileSettingsForm.input.fineGrid = uiSettingsDto.fineGrid ? uiSettingsDto.fineGrid : Math.trunc(25.0);
          savedProfileSettingsForm.input.sampleInterval = uiSettingsDto.sampleInterval ? uiSettingsDto.sampleInterval : 2;
          savedProfileSettingsForm.input.verticalInterval = uiSettingsDto.verticalInterval ? uiSettingsDto.verticalInterval : 5
          savedProfileSettingsForm.checked.showFineGrid = uiSettingsDto.showCoord ? uiSettingsDto.showCoord : false;

        }

        const uiStateType = response.data.uiStateType;

        if (uiStateType && TerraceUiState[uiStateType]) {
          terraceStore.uiState = uiStateType as UIState;
          this.proceedToPage(TerraceUiState[uiStateType].loadedPage);

        } else this.proceedToPage("input");

      } else this.proceedToPage("input");

    } catch (error) {
      console.error("Error refreshing elements:", error);
      this.proceedToPage("input");
    }

  }

  @action.bound newTerraceProject() {
    designStore.designElementTypeId = appConfig.hardCodes.designElementTypeTerrace; //To enable the PEM Toolbar in the map view when adding a new Terrace.
    terraceStore.reset();
    this.proceedToPage('input');
  }
  @action.bound async deleteSummaryStation(e: any, item: any) {
    const paramsID = item.alignedDesignElementId;
    try {
      this.isLoading = true;
      await TerraceDesignToolsController.deleteTerraceByAlignedElementDesignParamsIdUsingDELETE(paramsID);
      await this.getTerraceSummary();
    } catch (error) {
      console.error('error: deleteSummaryStation()', error)
    }
    finally {
      this.isLoading = false;
    }
  }

  elementActions = {
    'Edit': { action: this.editSummaryStation, disabled: false },
    'Delete': { action: this.deleteSummaryStation, disabled: false },
  };

  @action.bound async handleCheckboxChange(index: number) {
    const item = this.TerraceSummaryStationList[index];
    item.select = !item.select;
  }


  @action.bound async refreshElements() {
    if (this.mapView) {
      terraceStore.terraceLayers.forEach((layer) => {
        this.mapView!.map.remove(layer);
        layer.destroy();
      })
    }

    if (this.TerraceSummaryStationList.length > 0) {
      // const selectedTerraces = this.TerraceSummaryStationList.filter(e => e.select);
      const reversedTerraceList = [...this.TerraceSummaryStationList].reverse();

      // 🔹 Select only the checked terraces
      const selectedTerraces = reversedTerraceList.filter(e => e.select);
      if (selectedTerraces.length === 0) {
        if (this.mapView && !designStore.hideSingleCrossSection) designStore.validateSingleCrossSection(this.mapView);
        return;
      }
      try {
        // designStore.setLoader("Refreshing Elements...");
        this.isLoading = true;
        for (const terrace of selectedTerraces) {
          const terraceId = terrace.alignedDesignElementId;
          const terraceTitle = terrace.name;
          const response = await WaterwayDesignToolsController.getAlignedElementShapeByAlignedElementIdUsingGET(terraceId);

          if (response && response.data && response.data.alignedFeatureDesignShapeJson) {
            const channelResult = JSON.parse(response.data.alignedFeatureDesignShapeJson);
            if (!channelResult) {
              throw new Error("Failed to retrieve channel data from the service.");
            }
            if (this.mapView) {
              const layerTitle = (terraceTitle.length > 0 ? terraceTitle : `Terrace ${terraceId}`);
              terraceStore.addTerraceToMap(
                channelResult.terraceStructureShapeDTO,
                this.mapView,
                terraceId,
                layerTitle
              );
            }
          } else {
            console.error(`Failed to retrieve terrace data for ID ${terraceId}`);
          }
        }
      } catch (error) {
        console.error("Error refreshing elements:", error);
        MessageCenter.addError((error as Error).message);
      } finally {
        // designStore.setLoader("");
        this.isLoading = false;
        if (this.mapView && !designStore.hideSingleCrossSection) designStore.validateSingleCrossSection(this.mapView);
      }
    }
  }
}